import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import useAuthStore from './stores/authStore'
import useThemeStore from './stores/themeStore'

// Layout components
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'

// Admin components
import AdminGuard from './components/admin/AdminGuard'
import AdminLayout from './components/admin/AdminLayout'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import TranslationPage from './pages/TranslationPage'
import ReviewPage from './pages/ReviewPage'
import ReviewPageWorking from './pages/ReviewPageWorking'
import ReviewPageMinimal from './pages/ReviewPageMinimal'
import ReviewPageSimple from './pages/ReviewPageSimple'
import ReviewPageFixed from './pages/ReviewPageFixed'
import SettingsPage from './pages/SettingsPage'
import TMSIntegrationPage from './pages/TMSIntegrationPage'
import PaymentPage from './pages/PaymentPage'
import DocumentationPage from './pages/DocumentationPage'

// Admin Pages
import ProvidersPage from './pages/admin/ProvidersPage'
import InstructionsPage from './pages/admin/InstructionsPage'
import TranslationTestPage from './pages/admin/TranslationTestPage'
import QualityMatrixPage from './pages/admin/QualityMatrixPage'
import QualityBenchmarksPage from './pages/admin/QualityBenchmarksPage'
import LanguagesPage from './pages/admin/LanguagesPage'
import UsersPage from './pages/admin/UsersPage'
import StatisticsPage from './pages/admin/StatisticsPage'

function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth)
  const initializeTheme = useThemeStore((state) => state.initializeTheme)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)

  console.log('🏠 App component - isAuthenticated:', isAuthenticated)

  useEffect(() => {
    console.log('🚀 App initializing auth and theme')
    initializeAuth()
    initializeTheme()
  }, [initializeAuth, initializeTheme])

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route 
            path="/login" 
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            } 
          />
          <Route 
            path="/register" 
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/translate"
            element={
              <ProtectedRoute>
                <Layout>
                  <TranslationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tms-integration"
            element={
              <ProtectedRoute>
                <Layout>
                  <TMSIntegrationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route 
            path="/review/:jobId" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ReviewPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/review-original/:jobId" 
            element={
              <ProtectedRoute>
                <Layout>
                  <ReviewPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/payment" 
            element={
              <ProtectedRoute>
                <Layout>
                  <PaymentPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/docs" 
            element={
              <ProtectedRoute>
                <Layout>
                  <DocumentationPage />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Admin routes */}
          <Route
            path="/admin/providers"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <ProvidersPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/instructions"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <InstructionsPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/test"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <TranslationTestPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quality"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <QualityMatrixPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/quality-benchmarks"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <QualityBenchmarksPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/languages"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <LanguagesPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <UsersPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/statistics"
            element={
              <ProtectedRoute>
                <AdminGuard>
                  <AdminLayout>
                    <StatisticsPage />
                  </AdminLayout>
                </AdminGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={<Navigate to="/admin/providers" replace />}
          />

          {/* Debug route for testing */}
          <Route path="/debug" element={<div style={{padding: '20px', background: 'yellow'}}>DEBUG ROUTE WORKS! URL: {window.location.pathname}</div>} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </Router>
  )
}

export default App